import { GeographicInfoCountry } from "@/Common/Models/Localization/GeographicInfoCountry";
import { MedfarDictionariesValidationHelper } from "@/ContactDirectory/Helpers/MedfarDictionariesValidationHelper";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models//DirectoryFaxNumber";
import { DirectoryOtherEstablishment } from "@/ContactDirectory/Models//DirectoryOtherEstablishment";
import { DirectoryPhoneNumber } from "@/ContactDirectory/Models//DirectoryPhoneNumber";
import { BaseContactDirectoryValidator } from "./BaseContactDirectoryValidator";
import { IDirectoryAddressValidationResult } from "./DirectoryAddressValidator";

export interface IOtherEstablishmentValidationResult {
    name: boolean;
    address: IDirectoryAddressValidationResult;
    phoneNumbers: { isValid: boolean, isDuplicateInForm: boolean }[];
    faxNumbers: { isValid: boolean, isDuplicateInForm: boolean }[];
    email: boolean;
    isFormValid: boolean;
}

export class OtherEstablishmentValidator extends BaseContactDirectoryValidator {
    private emailRegex: RegExp;

    public constructor(validationHelper: MedfarDictionariesValidationHelper, geoCountries: GeographicInfoCountry[], emailRegex: RegExp) {
        super(validationHelper, geoCountries);
        this.emailRegex = emailRegex;
    }

    public validate(model: DirectoryOtherEstablishment): IOtherEstablishmentValidationResult {
        const isNameValid = !IsNullOrWhiteSpace(model.name);
        const addressValidResult = this.addressValidator.validate(model.address);

        const faxNumbers = model.faxNumbers;
        const phoneNumbers = model.phoneNumbers;

        // Keep track of phone numbers
        const areContactNumbersValid: { [index: string]: { isValid: boolean, isFax: boolean, isDuplicated: false } } = {};
        const faxNumbersValidity = this.getFaxNumbersValidity(faxNumbers, areContactNumbersValid);

        const isDuplicateNumberWithExtension: { [index: string]: boolean } = {};
        const phoneNumbersValidity = this.getPhoneNumberValidity(phoneNumbers, areContactNumbersValid, isDuplicateNumberWithExtension);

        // Never allow duplicates of fax numbers and phone numbers
        const areFaxNumbersValid = faxNumbersValidity.map(p => {
            const { contactNumber, isValid } = p;

            return {
                isValid: isValid,
                isDuplicateInForm: isValid
                    && areContactNumbersValid[`${this.countries[contactNumber.countryId]?.PhoneCode}-${contactNumber.number}`]?.isDuplicated
            };
        });

        // Allow duplicates of phone numbers with extension
        const arePhoneNumbersValid = phoneNumbersValidity.map(p => {
            const { contactNumber, isValid } = p;

            const isValidNumber = isValid
            && areContactNumbersValid[`${this.countries[contactNumber.countryId]?.PhoneCode}-${contactNumber.number}`]?.isValid;

            const isDuplicateWithFax = areContactNumbersValid[`${this.countries[contactNumber.countryId]?.PhoneCode}-${contactNumber.number}`]?.isDuplicated
                && areContactNumbersValid[`${this.countries[contactNumber.countryId]?.PhoneCode}-${contactNumber.number}`]?.isFax;

            return {
                isValid: isValidNumber,
                isDuplicateInForm: isValidNumber
                    && (
                        isDuplicateWithFax
                        || isDuplicateNumberWithExtension[`${this.countries[contactNumber.countryId]?.PhoneCode}-${contactNumber.number}-${contactNumber.extension ?? ""}`]
                    )

            };
        });

        const isEmailValid = IsNullOrWhiteSpace(model.email) || !!model.email.match(this.emailRegex)?.[0];

        const isFormValid = isNameValid
            && addressValidResult.isFormValid
            && model.faxNumbers.length > 0
            && areFaxNumbersValid.all(p => p.isValid && !p.isDuplicateInForm)
            && arePhoneNumbersValid.all(p => p.isValid && !p.isDuplicateInForm)
            && isEmailValid;

        return {
            name: isNameValid,
            address: addressValidResult,
            phoneNumbers: arePhoneNumbersValid,
            faxNumbers: areFaxNumbersValid,
            email: isEmailValid,
            isFormValid: isFormValid
        };
    }

    private getPhoneNumberValidity(
        phoneNumbers: DirectoryPhoneNumber[],
        areContactNumbersValid: { [index: string]: { isValid: boolean; isFax: boolean; isDuplicated: boolean }; },
        isDuplicatePhoneNumbersWithExtension: { [index: string]: boolean; }
    ): { isValid: boolean;contactNumber: DirectoryPhoneNumber; }[] {
        return phoneNumbers.map(p => {
            const isValid = !IsNullOrWhiteSpace(p.number)
                && this.isNumberValid(p);

            if (!isValid)
                return { isValid, contactNumber: p };

            const num = `${this.countries[p.countryId].PhoneCode}-${p.number}`;

            if (!areContactNumbersValid[num])
                areContactNumbersValid[num] = { isValid: true, isFax: false, isDuplicated: false };
            else
                areContactNumbersValid[num].isDuplicated = true;

            const numWithExt = `${this.countries[p.countryId].PhoneCode}-${p.number}-${p.extension ?? ""}`;

            isDuplicatePhoneNumbersWithExtension[numWithExt] = !IsNullOrUndefined(isDuplicatePhoneNumbersWithExtension[numWithExt]);

            return { isValid, contactNumber: p };
        });
    }

    private getFaxNumbersValidity(
        faxNumbers: DirectoryFaxNumber[],
        areContactNumbersValid: { [index: string]: { isValid: boolean; isFax: boolean; isDuplicated: boolean }; }
    ): { isValid: boolean; contactNumber: DirectoryFaxNumber; }[] {
        return faxNumbers.map(p => {
            const isValid = !IsNullOrWhiteSpace(p.number)
                && this.isNumberValid(p);

            if (!isValid)
                return { isValid, contactNumber: p };

            const num = `${this.countries[p.countryId].PhoneCode}-${p.number}`;
            if (!areContactNumbersValid[num])
                areContactNumbersValid[num] = { isValid: true, isFax: true, isDuplicated: false };
            else
                areContactNumbersValid[num] = { isValid: false, isFax: true, isDuplicated: true };

            return { isValid, contactNumber: p };
        });
    }
}
